import React from "react";
import { Box, Grid } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useData } from "../providers/DataProvider";
import ShopifyGetCollectionsForTab from "../utils/ShopifyGetCollectionsForTab";
import ShopifyGetCollection from "../utils/ShopifyGetCollection";
import FavoritesIconSrc from "../assets/favorites_icon.png";
import LettersNumbersIconSrc from "../assets/letters-numbers_icon.png";
import MessagingIconSrc from "../assets/messaging_icon.png";
import IconsIconSrc from "../assets/icons_icon_grayscale.png";
import SuperSizeIconSrc from "../assets/super-size_icon.png";
import QuestionIconSrc from "../assets/question_icon.png";
import ShopifyGetCollections from "../utils/ShopifyGetCollections";
import UseMobileMode from "../hooks/useMobileMode";
import CustomizerDrawerHeader from "./CustomizerDrawerHeader";
import CustomizerDrawerTabs from "./CustomizerDrawerTabs";
import PatchesSlider from "./PatchesSlider";
import CustomizerDrawerMenuPage from "./mobile/CustomizerDrawerMenuPage";

const sliderConfig = {
  slidesToShow: 15,
  slidesToScroll: 3,
  spacing: 5,
  breakpoints: [
    {
      width: 1601,
      slidesToShow: 12,
    },
    {
      width: 1201,
      slidesToShow: 9,
    },
    {
      width: 1025,
      slidesToShow: 6,
    },
    {
      width: 769,
      slidesToShow: 4,
    },
  ],
};

const lettersCollectionsOrder = ["Varsity White", "Mini Varsity", "Varsity Super Blue"];

const iconsCollectionsOrder = [
  "Smileys",
  "Hearts",
  "Stars",
  "Rainbows",
  "Peace",
  "Unicorns",
  "Dinos",
  "Animals",
  "Skate",
  "Food",
  "Gamer",
  "Music",
  "Crypto",
  "Sports",
  "Tennis",
  "Cheer",
  "Space",
  "Vehicles",
  "Logo",
];

const messagingCollectionsOrder = ["Voice", "Pride", "Planet Positive", "Equality"];

const StyledDrawerBackdrop = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  background: "#000000",
  opacity: 0.6,
}));

const StyledDrawer = styled(Box)(({ theme, sx }) => ({
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.up("laptop")]: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "25px 25px 0px 0px",
  },
  ...sx,
}));

const StyledTabsBox = styled(Box)(({ theme, sx }) => ({
  [theme.breakpoints.up("laptop")]: {
    paddingTop: 10,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("laptop")]: {
    height: "100%",
    paddingTop: 40,
    overflowY: "auto",
    paddingInline: 8,
    paddingBottom: 24,
    backgroundColor: theme.palette.background.default,
    ...sx,
  },
}));

const StyledTabsWrapper = styled(Box)(({ theme }) => ({
  "& .MuiTab-root": {
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "25px 25px 0px 0px",
    padding: "10px",
  },
  "& .MuiTab-root:first-of-type": {
    borderTopLeftRadius: "0px",
    borderLeft: "none !important",
  },
  "& .MuiTab-root:last-of-type": {
    borderTopRightRadius: "0px",
    borderRight: "none !important",
  },
  "& .MuiTab-root.Mui-selected": {
    backgroundColor: theme.palette.background.default,
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottomColor: theme.palette.background.default,
    color: "initial",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const StyledDrawerWrapper = styled(Box)(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  zIndex: 2,
  [theme.breakpoints.up("laptop")]: {
    bottom: 0,
  },
}));

const defaultConfig = {
  favorites: {
    label: "Favorites",
    icon: FavoritesIconSrc,
    collections: [],
    sort: [],
  },
  letters_numbers: {
    label: "Letters/Numbers",
    icon: LettersNumbersIconSrc,
    collections: [],
    sort: lettersCollectionsOrder,
  },
  messaging: {
    label: "Messaging",
    icon: MessagingIconSrc,
    collections: [],
    sort: messagingCollectionsOrder,
  },
  icons: {
    label: "Icons",
    icon: IconsIconSrc,
    collections: [],
    sort: iconsCollectionsOrder,
  },
  center_back: {
    label: "Super Size",
    icon: SuperSizeIconSrc,
    collections: [],
    sort: [],
  },
};

const convertToSnakeCase = (input) => input.toLowerCase().replace(/ /g, "_");

function CustomizerDrawer({
  setMobilePatch,
  setDrawerOpen,
  drawerOpen,
  favoritePatches,
  setFavoritePatches,
}) {
  const { specialAffiliate, audios, variant, characters } = useData();
  const theme = useTheme();
  const isMobile = UseMobileMode();
  const [initialCollections, setInitialCollections] = React.useState(null);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [selectedCollection, setSelectedCollection] = React.useState(0);
  const [selectedCol, setSelectedCol] = React.useState(0);
  const [filterValues] = React.useState([]);
  const [filteredCollections, setFilteredCollections] = React.useState([]);
  const [filteredPatches, setFilteredPatches] = React.useState([]);
  const [collectionsLoading, setCollectionsLoading] = React.useState(false);
  const [patchesLoading, setPatchesLoading] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [drawerConfig, setDrawerConfig] = React.useState(defaultConfig);

  // Effects.

  // Get collections.
  React.useEffect(() => {
    ShopifyGetCollections().then((collections) => {
      setInitialCollections(
        collections.map((collection) => ({
          id: collection.id,
          title: collection.title,
          categories: JSON.parse(collection.categories.value),
          patchImg: collection?.image?.src,
        }))
      );
    });
  }, []);

  // Set collections.
  React.useEffect(() => {
    if (!initialCollections) return;

    let specialDrawerItem = {};
    const hiddenSubcategories =
      specialAffiliate?.hidden_subcategories || characters?.hidden_subcategories;
    // filter patches via name, for example, key_collection=Bluey
    if (specialAffiliate?.key_collection || characters) {
      const keyCollection = specialAffiliate?.key_collection || characters?.name;
      const collections = getTabCollections(initialCollections, keyCollection, hiddenSubcategories);
      specialDrawerItem = collections.length
        ? {
            [convertToSnakeCase(keyCollection)]: {
              label: keyCollection,
              icon:
                initialCollections.find((c) => c.title === keyCollection)?.patchImg ??
                QuestionIconSrc,
              collections,
              sort: [],
            },
          }
        : {};
    }

    setDrawerConfig((state) => {
      const updatedConfig = {
        ...state,
        letters_numbers: {
          ...state.letters_numbers,
          collections: getTabCollections(
            initialCollections,
            "Letters/Numbers",
            hiddenSubcategories
          ),
        },
        messaging: {
          ...state.messaging,
          collections: getTabCollections(initialCollections, "Messaging", hiddenSubcategories),
        },
        icons: {
          ...state.icons,
          collections: getTabCollections(initialCollections, "Icons", hiddenSubcategories),
        },
        center_back: {
          ...state.center_back,
          collections: getTabCollections(initialCollections, "Center Back", hiddenSubcategories),
        },
        ...specialDrawerItem,
      };

      return Object.fromEntries(
        Object.entries(updatedConfig).filter(
          ([key, value]) =>
            key === "favorites" ||
            (Array.isArray(value.collections) && value.collections.length > 0)
        )
      );
    });

    if (!isMobile && !characters) {
      handleChangeTab(null, 1, true);
    }
  }, [initialCollections]);

  React.useEffect(() => {
    if (characters) {
      const index = Object.values(drawerConfig).findIndex((el) => el.label === characters?.name);

      handleChangeTab(null, index === -1 ? 1 : index, true);
    }
  }, [characters, drawerConfig]);

  // Set patches.
  React.useEffect(() => {
    if (initialCollections) {
      if (selectedTab === 0) {
        setFilteredCollections([]);
        setFilteredPatches(filterPatches(favoritePatches));
      }
      if (selectedTab >= 1) {
        const selectedTabCols = Object.values(drawerConfig)[selectedTab].collections;

        setCollectionsLoading(true);
        setPatchesLoading(true);
        ShopifyGetCollectionsForTab(selectedTabCols.map((e) => e.title)).then((collections) => {
          const sortedCols = sortCollections(collections);
          const filteredCols = filterCollections(sortedCols);
          setFilteredCollections(filteredCols);
          setSelectedCollection(isMobile ? null : 0);
          setSelectedCol(isMobile ? null : filteredCollections[0]);
          setCollectionsLoading(false);

          if (filteredCols[0]) {
            loadCollectionPatches(filteredCols[0].id);
          } else {
            setFilteredPatches([]);
            setPatchesLoading(false);
          }
        });
      }
    }
  }, [initialCollections, selectedTab, filterValues]);

  React.useEffect(() => {
    if (hasLoaded) {
      setDrawerOpen(false);
      if (isMobile) {
        setSelectedTab(null);
        setSelectedCategory(null);
        setSelectedCol(null);
        setSelectedCollection(null);
      } else {
        setSelectedTab(1);
        setSelectedCategory(Object.values(drawerConfig)[1]);
        setSelectedCol(filteredCollections[0]);
        setSelectedCollection(0);
      }
    } else {
      setHasLoaded(true);
    }
  }, [isMobile]);

  // Handlers.

  const handleChangeTab = React.useCallback(
    (event, value, render = false) => {
      !render && audios.SELECTING_CATEGORY.play();
      setSelectedTab(value);
      setSelectedCategory(Object.values(drawerConfig)[value]);
    },
    [drawerConfig, selectedTab, selectedCategory]
  );

  const handleChangeCollection = React.useCallback(
    (index) => {
      audios.SELECTING_SUBCATEGORY.play();
      setSelectedCollection(index);
      setSelectedCol(filteredCollections[index] ?? null);
      const selectedCol = filteredCollections[index];
      if (!selectedCol) return;
      if (selectedTab === 0) {
        setFilteredPatches(filterPatches(selectedCol.patches));
      }
      if (selectedTab >= 1) {
        loadCollectionPatches(selectedCol.id);
      }
    },
    [selectedCollection, filteredCollections, patchesLoading]
  );

  const handleFavoritePatch = (patch) => {
    audios.SELECT_FAVORITE_PATCH.play();

    const favorites = favoritePatches.some((e) => e.id === patch.id)
      ? favoritePatches.filter((e) => e.id !== patch.id)
      : favoritePatches.concat(patch);
    setFavoritePatches(favorites);

    if (isMobile && favorites.length) {
      setMobilePatch(favorites[0]);
    }

    if (selectedTab === 0) {
      setFilteredPatches(filterPatches(favorites));
    }
  };

  // Helpers.

  const filterCollections = (collections) => {
    return collections
      .filter((col) =>
        filterValues.length
          ? col.patches.some((patch) => filterValues.includes(patch.points))
          : true
      )
      .filter((col) =>
        variant.underThree ? col.patches.some((patch) => !patch.material.includes("Sequin")) : true
      );
  };

  const filterPatches = (patches) => {
    return patches
      .filter((patch) => (filterValues.length ? filterValues.includes(patch.points) : true))
      .filter((patch) => (variant.underThree ? !patch.material.includes("Sequin") : true))
      .filter((patch) => (selectedTab === 4 ? patch.centerback : true));
  };

  const loadCollectionPatches = (id) => {
    setPatchesLoading(true);
    ShopifyGetCollection(id).then((collection) => {
      setFilteredPatches(filterPatches(collection.patches));
      setPatchesLoading(false);
    });
  };

  const getTabCollections = (collections, category, hiddenList) => {
    return collections
      .filter((e) => e.categories.includes(category))
      .filter((e) => !hiddenList || !hiddenList.includes(e.title));
  };

  const sortCollections = (collections) => {
    const order = Object.values(drawerConfig)[selectedTab].sort;
    return collections.sort((a, b) => {
      const orderA = order.indexOf(a.title);
      const orderB = order.indexOf(b.title);
      if (orderA === -1) return 1;
      if (orderB === -1) return -1;
      return orderA - orderB;
    });
  };

  const getDesignButtonText = () => {
    if (drawerOpen) {
      if (!isMobile) {
        return "Start Designing!";
      }

      return selectedCol
        ? selectedCol.title
        : selectedCategory
        ? selectedCategory.label
        : "Patches";
    } else {
      return "More Patches";
    }
  };

  const selectPrevCategory = (e) => {
    e.stopPropagation();
    if (selectedCol === null) {
      selectCategory(-1);
    } else {
      selectCollection(-1);
    }
  };

  const selectNextCategory = (e) => {
    e.stopPropagation();
    if (selectedCol === null) {
      selectCategory(1);
    } else {
      selectCollection(1);
    }
  };

  const selectCategory = (direction) => {
    const categoryCount = Object.values(drawerConfig).length;
    const newSelectedTab = (selectedTab + direction + categoryCount) % categoryCount;
    handleChangeTab(null, newSelectedTab);
  };

  const selectCollection = (direction) => {
    const collectionCount = selectedCategory.collections.length;
    const newSelectedCollection =
      (selectedCollection + direction + collectionCount) % collectionCount;
    handleChangeCollection(newSelectedCollection);
  };

  const handleSetMobilePatch = (index) => {
    const patch = filteredPatches[index];
    setMobilePatch(patch);
    setDrawerOpen(false);
    setSelectedTab(null);
    setSelectedCol(0);
  };

  // Render.
  return (
    <StyledDrawerWrapper
      sx={{
        [theme.breakpoints.down("laptop")]: {
          bottom: drawerOpen ? 0 : "auto",
          top: drawerOpen ? 100 : 72,
        },
      }}
    >
      {drawerOpen && !isMobile && <StyledDrawerBackdrop />}

      <StyledDrawer
        sx={
          drawerOpen
            ? {
                backgroundColor: theme.palette.background.paper,
                boxShadow: "0px 3px 23px 1px #0000000F",
                [theme.breakpoints.down("laptop")]: {
                  height: "100%",
                },
              }
            : {}
        }
      >
        <CustomizerDrawerHeader
          drawerOpen={drawerOpen}
          selectedTab={selectedTab}
          buttonText={getDesignButtonText()}
          selectPrevCategory={selectPrevCategory}
          selectNextCategory={selectNextCategory}
          setSelectedCategory={setSelectedCategory}
          setSelectedCol={setSelectedCol}
          setDrawerOpen={setDrawerOpen}
          setSelectedTab={setSelectedTab}
          setSelectedCollection={setSelectedCollection}
        />
        {drawerOpen && (
          <>
            <StyledTabsBox sx={{ paddingInline: selectedTab === null ? "24px" : "8px" }}>
              <StyledTabsWrapper>
                {!isMobile ? (
                  <CustomizerDrawerTabs
                    handleChangeTab={handleChangeTab}
                    drawerConfig={drawerConfig}
                    selectedTab={selectedTab}
                    sliderConfig={sliderConfig}
                    collectionsLoading={collectionsLoading}
                    filteredCollections={filteredCollections}
                    selectedCollection={selectedCollection}
                    handleChangeCollection={handleChangeCollection}
                    filterValues={filterValues}
                  />
                ) : (
                  <Grid container columns={3} spacing="9px" sx={{ width: "auto" }}>
                    {selectedTab === null && (
                      // Select category
                      <CustomizerDrawerMenuPage
                        columns={2}
                        firstItemWide={true}
                        items={Object.values(drawerConfig)}
                        selectedItemIndex={selectedTab}
                        handleCardClick={(e) => handleChangeTab(null, e)}
                      />
                    )}
                    {selectedTab !== null && (
                      <>
                        {selectedCollection === null && selectedTab !== 0 && (
                          // Select collection
                          <CustomizerDrawerMenuPage
                            loading={collectionsLoading}
                            items={collectionsLoading ? [{}] : [...filteredCollections]}
                            selectedItemIndex={selectedCollection}
                            handleCardClick={handleChangeCollection}
                            icon="src"
                            label="title"
                          />
                        )}

                        {(selectedCollection !== null || selectedTab === 0) && (
                          // Choose patch
                          <CustomizerDrawerMenuPage
                            isPatch={true}
                            isFavorites={selectedTab === 0}
                            loading={patchesLoading}
                            items={patchesLoading ? [{}] : [...filteredPatches]}
                            handleCardClick={(e) => handleSetMobilePatch(e)}
                            icon="src"
                            favoritePatches={favoritePatches}
                            handleFavoritePatch={handleFavoritePatch}
                          />
                        )}
                      </>
                    )}
                  </Grid>
                )}
              </StyledTabsWrapper>
            </StyledTabsBox>
          </>
        )}
        {!isMobile && (
          <PatchesSlider
            patchesLoading={patchesLoading}
            filteredPatches={filteredPatches}
            filterValues={filterValues}
            selectedTab={selectedTab}
            favoritePatches={favoritePatches}
            sliderConfig={sliderConfig}
            handleFavoritePatch={handleFavoritePatch}
          />
        )}
      </StyledDrawer>
    </StyledDrawerWrapper>
  );
}

export default React.memo(CustomizerDrawer);
